import { version as PDFLibFontkitVersion } from '@pdf-lib/fontkit/package.json'
import { version as PDFLibVersion } from 'pdf-lib/package.json'
import { version as pdfjsVersion } from 'pdfjs-dist/package.json'
import type { DocumentInitParameters } from 'pdfjs-dist/types/src/display/api'

export class InsufficientCanvasMemoryError extends Error {
  constructor() {
    super('insufficient canvas memory.')
  }
}

class CanvasFactory {
  create(width: number, height: number) {
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d')
    if (!context) {
      throw new InsufficientCanvasMemoryError()
    }
    return { canvas, context }
  }
  reset(canvasAndContext: { canvas: HTMLCanvasElement; context: CanvasRenderingContext2D }, width: number, height: number) {
    canvasAndContext.canvas.width = width
    canvasAndContext.canvas.height = height
  }
  destroy(canvasAndContext: { canvas: HTMLCanvasElement | null; context: CanvasRenderingContext2D | null }) {
    canvasAndContext.canvas!.width = 0
    canvasAndContext.canvas!.height = 0
    canvasAndContext.canvas = null
    canvasAndContext.context = null
  }
}

let pdfjs$: Promise<typeof import('pdfjs-dist')>
const importPdfjs = () =>
  (pdfjs$ ??= import('pdfjs-dist').then(pdfjs => {
    pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.mjs?${pdfjsVersion}`
    return pdfjs
  }))

export const loadPdfDocument = async (data: NonNullable<DocumentInitParameters['data']>) => {
  const pdfjs = await importPdfjs()
  // オプション `isEvalSupported: false` は悪意のある PDF を開くだけで任意コード実行されてしまう脆弱性対策です。
  // 参考: https://codeanlabs.com/blog/research/cve-2024-4367-arbitrary-js-execution-in-pdf-js/
  //       https://github.com/mozilla/pdf.js/security/advisories/GHSA-wgrm-67xf-hhpq
  return await pdfjs.getDocument({ data, cMapUrl: '/cmaps/', cMapPacked: true, isEvalSupported: false, CanvasFactory }).promise
}

let pdfLib$: Promise<typeof import('pdf-lib')>
const importPdfLib = () => (pdfLib$ ??= import(`/pdf-lib.esm.min.js?${PDFLibVersion}`) as Promise<typeof import('pdf-lib')>)

const createPdfDocumentWithoutFontkit = async () => {
  const { PDFDocument } = await importPdfLib()
  return await PDFDocument.create({ updateMetadata: false })
}

export const createPdfDocument = async (options?: { fontkit: boolean }) => {
  if (options?.fontkit) {
    const [doc, fontkit] = await Promise.all([
      createPdfDocumentWithoutFontkit(),
      import(`/@pdf-lib/fontkit.js?${PDFLibFontkitVersion}`) as Promise<{ default: typeof import('@pdf-lib/fontkit') }>,
    ])
    doc.registerFontkit(fontkit.default)
    return doc
  } else {
    return createPdfDocumentWithoutFontkit()
  }
}
